import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  StickyHeaderNavigation,
  PhoneNumberInput,
  ChooseMessagingAppModal,
} from '@/components'
import { useProfileUpdateStore } from '@/store/profileUpdate'
import { useSendPhoneVerificationCode } from '@/domain/identity/useCases'
import { responsiveAdapter, responsiveAdapterVertical } from '@/utils'
import { inputLimits } from '@/constants/inputLimits'
import { toast } from 'react-hot-toast'
import useOffsets from '@/hooks/useOffsets'

// Phone number types (as in React Native)
interface Phone {
  number: string
  countryCode: string
  countryIsoCode: string
}

// Utility for cleaning phone number
const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/\s+/g, '').replace(/[^\d+]/g, '')
}

// Simple phone number validation
const validatePhoneNumber = (
  phone: string,
  countryCode: string,
): { isValid: boolean } => {
  const cleanPhone = cleanPhoneNumber(phone)
  // Simple check: number must contain 7 to 15 digits
  const isValid = cleanPhone.length >= 7 && cleanPhone.length <= 15
  return { isValid }
}

export const UpdatePhoneNumberScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offset = useOffsets()
  const [phoneNumberWithCode, setPhoneNumberWithCode] = useState<Phone>()
  const [isChooseMessagingAppModalOpen, setIsChooseMessagingAppModalOpen] =
    useState(false)

  // Store state (as in React Native)
  const {
    newPhoneNumber,
    phoneErrorMessage,
    phoneIsValid,
    newPhoneMessagingMethodCode,
    setProfileUpdateFormState,
  } = useProfileUpdateStore()

  // API for sending verification code
  const { mutate: sendPhoneVerificationCode, isPending: isSendingCode } =
    useSendPhoneVerificationCode({
      onSuccess: () => {
        if (phoneErrorMessage?.length) {
          resetPhoneFormState()
        }
        // Navigate to verification page
        navigate('/profile/update-phone-number/verify')
      },
      onError: ({ errorMessage }) => {
        if (errorMessage) {
          toast.error(errorMessage)
        }
      },
    })

  const resetPhoneFormState = () => {
    setProfileUpdateFormState({
      phoneErrorMessage: '',
      phoneIsValid: true,
    })
  }

  // Phone number validation (as in React Native)
  const checkPhone = useCallback(
    (phone: Phone) => {
      if (phoneNumberWithCode) {
        const completePhone = cleanPhoneNumber(
          `${phone?.countryCode}${phone?.number}`,
        )

        const { isValid } = validatePhoneNumber(
          completePhone,
          phone.countryIsoCode!,
        )

        if (!isValid) {
          setProfileUpdateFormState({
            phoneErrorMessage: t('api:errors.invalidPhone'),
            phoneIsValid: false,
            newPhoneNumber: '',
          })
        } else {
          resetPhoneFormState()
          setProfileUpdateFormState({
            newPhoneNumber: completePhone,
            phoneIsValid: true,
          })
        }
      }
    },
    [phoneNumberWithCode, setProfileUpdateFormState, t],
  )

  // Debounced number validation (as in React Native)
  const debounceOnChangeTextInput = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout
      return ({ phone }: { phone: Phone }) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          if (phone?.number && phone?.countryIsoCode) {
            checkPhone(phone)
          }
        }, 500)
      }
    })(),
    [checkPhone],
  )

  // Handle code submission (as in React Native)
  const handleSubmit = () => {
    sendPhoneVerificationCode({
      phone_number: newPhoneNumber || '',
      send_method: newPhoneMessagingMethodCode || 'sms',
    })
  }

  // Handle phone number change (as in React Native)
  const handlePhoneChange = (phone: Phone) => {
    const completePhone = `${phone.countryCode}${phone.number}`.replace(
      /\s+/g,
      '',
    )

    // Reset validation if number changed
    if (phoneIsValid && completePhone !== newPhoneNumber) {
      resetPhoneFormState()
    }

    setPhoneNumberWithCode(phone)
    debounceOnChangeTextInput({ phone })
  }

  // Handle SMS delivery method selection
  const handleMessagingMethodSelect = ({
    code,
    img_url,
  }: {
    code: string
    img_url: string
  }) => {
    setProfileUpdateFormState({
      newPhoneMessagingMethodCode: code as any,
      newPhoneMessagingMethodImgUrl: img_url,
    })

    // Send code after 200ms (as in React Native)
    setTimeout(handleSubmit, 200)
  }

  return (
    <BaseView
      style={{
        flex: 1,

        paddingTop: 16,
        height: '100%',
      }}
    >
      {/* Header */}
      <StickyHeaderNavigation
        title={t(
          'screens:profile.profileSettings.items.editProfile.updatePhoneNumber',
        )}
      />

      <BaseView
        px={13}
        style={{
          flex: 1,
          paddingTop: offset.top + 70,
        }}
      >
        {/* Content */}
        <BaseView style={{ gap: responsiveAdapterVertical(16) }}>
          {/* Description */}
          <BaseText fontSize={16} fontFamily="regular" color="label.secondary">
            {t('translation:editPhoneNumber.enterTheNewPhoneNumber')}
          </BaseText>

          {/* Phone Number Input */}
          <PhoneNumberInput
            errorMessage={phoneErrorMessage}
            withoutBorder={true}
            value={phoneNumberWithCode}
            onChange={handlePhoneChange}
          />
        </BaseView>

        {/* Footer */}
        <BaseView style={{ marginTop: 'auto', paddingBottom: offset.bottom }}>
          <AppButton
            onPress={() => {
              setIsChooseMessagingAppModalOpen(true)
            }}
            label={t('form:actions.update')}
            disabled={!phoneIsValid}
            isLoading={isSendingCode}
          />
        </BaseView>
      </BaseView>

      {/* Choose Messaging App Modal */}
      <ChooseMessagingAppModal
        isOpen={isChooseMessagingAppModalOpen}
        onClose={() => setIsChooseMessagingAppModalOpen(false)}
        onSelect={handleMessagingMethodSelect}
      />
    </BaseView>
  )
}
